import { z } from 'zod'

const EnumSchema = z.object({
  id: z.number(),
  name: z.string(),
})

const RegionSchema = z.object({
  id: z.number(),
  name: z.string(),
  slug: z.string(),
})

// Location category enum, like EnumSchema but with a `slug` for SEO landing
// pages. `slug` is optional so /enum still parses before the backend deploy
// that adds it.
const LocationCategorySchema = z.object({
  id: z.number(),
  name: z.string(),
  slug: z.string().optional(),
})

const AddressSchema = z.object({
  street: z.string(),
  city: z.string(),
  zip: z.string(),
  latitude: z.number(),
  longitude: z.number(),
})

// What the unit itself offers (the room, apartment or the whole house).
const UnitFacilitySchema = z.object({
  bathroom: z.object({
    bathtub: z.boolean(),
    shower: z.boolean(),
    towels: z.boolean(),
    toiletries: z.boolean(),
    hairdryer: z.boolean(),
  }),
  bedroom: z.object({
    doubleBed: z.boolean(),
    twinBeds: z.boolean(),
    childCrib: z.boolean(),
  }),
  kitchen: z.object({
    dishwasher: z.boolean(),
    microwave: z.boolean(),
    oven: z.boolean(),
    refrigerator: z.boolean(),
    stove: z.boolean(),
    dishes: z.boolean(),
    basicCookingIngredients: z.boolean(),
    highChair: z.boolean(),
    automaticCoffeeMachine: z.boolean(),
    nespressoCoffeeMachine: z.boolean(),
    filterCoffeeMaker: z.boolean(),
  }),
  general: z.object({
    airConditioning: z.boolean(),
    indoorFireplace: z.boolean(),
    woodHeating: z.boolean(),
    washingMachine: z.boolean(),
    internet: z.boolean(),
    tv: z.boolean(),
  }),
})

// What the whole place offers, shared by all of its units.
const LocationFacilitySchema = z.object({
  garden: z.boolean(),
  terrace: z.boolean(),
  pool: z.boolean(),
  whirlpool: z.boolean(),
  hotTub: z.boolean(),
  sauna: z.boolean(),
  fitness: z.boolean(),
  outdoorFireplace: z.boolean(),
  grill: z.boolean(),
  freeParking: z.boolean(),
  restaurant: z.boolean(),
  breakfastIncluded: z.boolean(),
  bar: z.boolean(),
  sportFacility: z.boolean(),
  childrenPlayground: z.boolean(),
})

const ImageSchema = z.object({
  description: z.string(),
  priority: z.number(),
  url: z.object({
    original: z.string(),
    thumbnail: z.string(),
  }),
})

const TripSchema = z.object({
  title: z.string(),
  // Link to the trip detail on kudyznudy.cz. Nullish for trips generated before
  // the backend started storing it.
  url: z.string().nullish(),
  description: z.string(),
  distance: z.number(),
  imageUrl: z.string(),
})

export const SiteKeySchema = z.enum([
  'airbnb',
  'amazingplaces',
  'booking',
  'cschalupy',
  'echalupy',
  'google',
  'kouzelnechaloupky',
])

export const SiteSchema = z.object({
  site: SiteKeySchema,
  ratingValue: z.number().nullable(),
  ratingScale: z.number().nullish(),
  ratingCount: z.number().int().nullable(),
  // Average nightly price on the platform; null when unknown. Optional so the
  // detail endpoint parses until the backend starts returning it.
  averagePrice: z.number().nullish(),
})

// A space (room) of a unit with its beds and bathroom fittings.
const SpaceSchema = z.object({
  type: z.string(),
  equipment: z.array(z.string()).optional(),
  singleBeds: z.number(),
  doubleBeds: z.number(),
  childCribs: z.number(),
  bathtubs: z.number(),
  showers: z.number(),
  toilets: z.number(),
})

// A bookable offer window with its per-night price range. `id` is the offer
// UUID, matching the Meta catalog feed's `g:id` — used as `content_ids` for
// dynamic ads. Dates and prices feed the JSON-LD offers in the SEO head and
// the Markdown detail for agents.
const OfferWindowSchema = z.object({
  id: z.string(),
  start: z.string(),
  end: z.string(),
  minDiscountPrice: z.number(),
  maxDiscountPrice: z.number(),
  standardPrice: z.number(),
  minNights: z.number(),
})

// What a guest books: a room, an apartment or the whole place. Every location
// has at least one unit; a cottage has a single one that carries the location
// name (`name` falls back to it when the unit has none of its own).
export const UnitSchema = z.object({
  id: z.number(),
  name: z.string(),
  // Unique within the location only — the detail lives at
  // /ubytovani/<location.slug>/<unit.slug>.
  slug: z.string(),
  description: z.string(),
  quantity: z.number(),
  // Order within the location, ascending; siblings come sorted by it.
  position: z.number(),
  // False for a paused or not yet approved unit (a preview): it can be shown
  // but not booked, so the page is noindex and offers no calendar.
  sellable: z.boolean(),
  totals: z.object({
    bedrooms: z.number(),
    beds: z.number(),
    bathrooms: z.number(),
    cribs: z.number(),
  }),
  type: EnumSchema,
})

// instant: the payment books the stay; request: the money is only held on the
// card until the owner accepts. Falls back to instant for an API that predates
// it (stage runs develop against the production API).
export const BookingModeSchema = z.enum(['instant', 'request']).catch('instant')

export type BookingMode = z.infer<typeof BookingModeSchema>

export const LocationDetailSchema = z.object({
  id: z.number(),
  slug: z.string(),
  name: z.string(),
  category: LocationCategorySchema,
  region: RegionSchema,
  labels: z.array(EnumSchema).optional(),
  address: AddressSchema,
  photos: z.array(ImageSchema).optional(),
  // Optional until the backend change shipping this field is deployed
  offerAheadDays: z.number().optional(),
  bookingMode: BookingModeSchema,

  description: z.object({
    short: z.string(),
    detailed: z.string(),
    transport: z.string(),
  }),
  conditions: z.object({
    pets: z.boolean(),
    adult: z.boolean(),
    parties: z.boolean(),
    smoking: z.boolean(),
  }),
  check: z.object({
    inType: EnumSchema,
    outType: EnumSchema,
    in: z.string(),
    out: z.string(),
  }),
  surcharges: z.array(
    z.object({
      type: z.string(),
      unit: z.string(),
      price: z.number(),
    }),
  ),
  surchargePayments: z.array(EnumSchema),

  facility: LocationFacilitySchema,

  business: z.object({
    name: z.string(),
    cin: z.string(),
  }),

  trips: z.array(TripSchema).optional(),
  sites: z.array(SiteSchema).optional(),

  // True when this location's calendar rarely has openings. Optional so the
  // detail endpoint parses until the backend starts returning it.
  rareFind: z.boolean().optional(),

  // Generated og:image banner for social previews, loaded via the `ogImage`
  // scope. Null until the backend generates one for this location.
  ogImage: z
    .object({
      url: z.string(),
      width: z.number(),
      height: z.number(),
    })
    .nullish(),
})

// `GET /location/<slug>/unit/<unitSlug>` with the unit's own data and its
// location nested — the backing of the detail, gallery and reservation pages.
export const UnitDetailSchema = UnitSchema.extend({
  location: LocationDetailSchema,
  facility: UnitFacilitySchema,
  spaces: z.array(SpaceSchema),
  // The unit's own photos; empty means show the location photos.
  photos: z.array(ImageSchema),
  // The other active units of the same location.
  siblings: z.array(UnitSchema),
  offers: z.array(OfferWindowSchema),
  // Generated og:image banner from the unit's own photos. Null for a unit
  // without photos of its own — the location banner applies then.
  ogImage: z
    .object({
      url: z.string(),
      width: z.number(),
      height: z.number(),
    })
    .nullish(),
})

export type Unit = z.infer<typeof UnitSchema>
export type UnitDetail = z.infer<typeof UnitDetailSchema>

// The detail pages render a place seen through one of its units: the location
// stays the root (id, slug, name, description, conditions… are the place's),
// the unit sold on the page hangs off `unit`. Reshaped from UnitDetail in
// `accommodationDetailFetch`.
export type AccommodationDetail = LocationDetail & {
  unit: Omit<UnitDetail, 'location'>
}

// `GET /location/<slug>?scope=photos,units,units.photos` — enough to redirect a location
// URL to its first unit and to render a location card.
export const LocationSummarySchema = z.object({
  id: z.number(),
  slug: z.string(),
  name: z.string(),
  region: RegionSchema,
  address: AddressSchema,
  photos: z.array(ImageSchema).optional(),
  // Active units only, in the owner's order.
  units: z.array(
    UnitSchema.extend({
      // The unit's own photos; empty means show the location photos.
      photos: z.array(ImageSchema).optional(),
    }),
  ),
})

export type LocationSummary = z.infer<typeof LocationSummarySchema>

// `GET /unit/<id>?scope=photos` — the unit's own photos for its og:image card.
export const UnitPhotosSchema = UnitSchema.extend({
  photos: z.array(ImageSchema),
})

export type UnitPhotos = z.infer<typeof UnitPhotosSchema>

// Slim `/metadata?scope=location` item for the public catalog page and the
// sitemap: every sellable unit, including ones without a currently bookable
// offer, so each detail keeps at least one internal link.
export const CatalogItemSchema = z.object({
  slug: z.string(),
  name: z.string(),
  location: z.object({
    slug: z.string(),
    name: z.string(),
    region: z.object({ name: z.string() }),
    category: z.object({ name: z.string() }),
    address: z.object({ city: z.string() }),
  }),
})

export type CatalogItem = z.infer<typeof CatalogItemSchema>

export const LocationSchema = z.object({
  id: z.number(),
  slug: z.string(),
  name: z.string(),
  category: EnumSchema,
  region: RegionSchema,
  address: AddressSchema,
  bookingMode: BookingModeSchema,
  photos: z.array(ImageSchema).optional(),

  description: z.object({
    short: z.string(),
    detailed: z.string(),
    transport: z.string(),
  }),
  surcharges: z.array(
    z.object({
      type: z.string(),
      unit: z.string(),
      price: z.number(),
    }),
  ),
  surchargePayments: z.array(EnumSchema),
  business: z.object({
    name: z.string(),
    cin: z.string(),
  }),
})

const LocationSearchResultSchema = z.object({
  id: z.number(),
  slug: z.string(),
  name: z.string(),
  category: EnumSchema,
  region: RegionSchema,
  address: AddressSchema,
  bookingMode: BookingModeSchema,
  photos: z.array(ImageSchema).optional(),
  sites: z.array(SiteSchema).optional(),
})

// One offer per unit: a location with several units shows up once per unit.
export const SearchResultSchema = z.object({
  start: z.coerce.date(),
  end: z.coerce.date(),
  minDiscountPrice: z.number(),
  maxDiscountPrice: z.number(),
  standardPrice: z.number(),
  minNights: z.number(),
  rareFind: z.boolean().optional(),
  location: LocationSearchResultSchema,
  unit: UnitSchema.extend({
    // The unit's own photos; empty means show the location photos.
    photos: z.array(ImageSchema).optional(),
  }),
})

// GET /offer/similar: offers that miss one or two of the search's criteria,
// each with what it misses.
export const SimilarOfferDifferenceSchema = z.discriminatedUnion('type', [
  z.object({ type: z.literal('facility'), facility: z.string() }),
  z.object({ type: z.literal('label'), name: z.string() }),
  z.object({ type: z.literal('category'), name: z.string() }),
  z.object({ type: z.literal('region'), name: z.string() }),
  z.object({
    type: z.literal('date'),
    shift: z.number(),
    arrival: z.coerce.date(),
    departure: z.coerce.date(),
  }),
])

export const SimilarOfferSchema = z.object({
  offer: SearchResultSchema,
  differences: z.array(SimilarOfferDifferenceSchema),
})

export const UnitAvailabilitySchema = z.array(
  z.object({
    date: z.coerce.date(),
    available: z.boolean(),
    standardPrice: z.number(),
    discountPrice: z.number(),
    commissionPrice: z.number(),
    minNights: z.number(),
    // Defaults mean "no rule"; they keep the schema parsing an API that
    // predates the stay rules (stage runs develop against the production API).
    maxNights: z.number().nullable().default(null),
    closedToArrival: z.boolean().default(false),
    closedToDeparture: z.boolean().default(false),
    state: z.string(),
    departures: z.array(z.string()).optional(),
  }),
)

// Common
export type Address = z.infer<typeof AddressSchema>
export type Image = z.infer<typeof ImageSchema>
export type Trip = z.infer<typeof TripSchema>
export type Site = z.infer<typeof SiteSchema>
export type SiteKey = z.infer<typeof SiteKeySchema>

// Location detail
export type LocationDetail = z.infer<typeof LocationDetailSchema>
export type Space = z.infer<typeof SpaceSchema>
export type UnitFacility = z.infer<typeof UnitFacilitySchema>
export type LocationFacility = z.infer<typeof LocationFacilitySchema>
export type Conditions = z.infer<typeof LocationDetailSchema>['conditions']
export type Surcharges = z.infer<typeof LocationDetailSchema>['surcharges']
export type Business = z.infer<typeof LocationDetailSchema>['business']

export type SurchargePayments = z.infer<typeof LocationDetailSchema>['surchargePayments']

// Enums
export const CountryCodeSchema = z.object({
  id: z.number(),
  name: z.string(),
  prefix: z.string(),
  flag: z.string(),
})

export const OrderStateSchema = z.object({
  id: z.number(),
  state: z.string(),
  name: z.string(),
})

export const EnumResponseSchema = z.object({
  regions: z.array(RegionSchema),
  unitTypes: z.array(EnumSchema),
  locationCategories: z.array(LocationCategorySchema),
  labels: z.array(EnumSchema),
  checkInTypes: z.array(EnumSchema),
  checkOutTypes: z.array(EnumSchema),
  countryCodes: z.array(CountryCodeSchema),
  orderStates: z.array(OrderStateSchema),
  surchargeTypes: z.array(EnumSchema),
  surchargeUnits: z.array(EnumSchema),
  surchargePayments: z.array(EnumSchema),
})

export type EnumResponse = z.infer<typeof EnumResponseSchema>

//Search
export type LocationSearchResult = z.infer<typeof SearchResultSchema>
export type SimilarOffer = z.infer<typeof SimilarOfferSchema>
export type SimilarOfferDifference = z.infer<typeof SimilarOfferDifferenceSchema>
export type UnitAvailability = z.infer<typeof UnitAvailabilitySchema>

//Order summary
export const OrderSummaryRequestSchema = z.object({
  start: z.coerce.date(),
  end: z.coerce.date(),
  unitId: z.number(),
  pets: z.boolean(),
  beds: z.int(),
  cribs: z.int(),
})

export const OrderSummaryResponseSchema = z.object({
  standardPrice: z.coerce.number(),
  discountPrice: z.coerce.number(),
  // A location confirming each booking: until when its owner would answer a
  // request sent now (Prague time, "yyyy-MM-dd HH:mm:ss"). Null otherwise.
  requestExpiresAt: z.string().nullish(),
})

export type OrderSummaryRequest = z.infer<typeof OrderSummaryRequestSchema>
export type OrderSummaryResponse = z.infer<typeof OrderSummaryResponseSchema>

// Discriminated union for order summary result
export type OrderSummaryResult =
  | {
      available: true
      standardPrice: number
      discountPrice: number
      requestExpiresAt: string | null
    }
  | { available: false }

// Order draft
export const OrderDraftRequestSchema = z.object({
  uuid: z.uuid(),
  unitId: z.number().int(),
  start: z.string(),
  end: z.string(),
  firstName: z.string().nullable(),
  lastName: z.string().nullable(),
  email: z.string().nullable(),
  countryCodeId: z.number().int().nullable(),
  phone: z.string().nullable(),
})

export const OrderDraftResponseSchema = z.object({
  uuid: z.uuid(),
})

export type OrderDraftRequest = z.infer<typeof OrderDraftRequestSchema>
export type OrderDraftResponse = z.infer<typeof OrderDraftResponseSchema>

// Order
export const OrderRequestSchema = z.object({
  unitId: z.number(),
  start: z.string(),
  end: z.string(),
  firstName: z.string(),
  lastName: z.string(),
  email: z.email(),
  phone: z.string(),
  countryCodeId: z.number(),
  pets: z.boolean(),
  beds: z.number().int(),
  cribs: z.number().int(),
  invoiceCompany: z.string().nullable().optional(),
  invoiceCin: z.string().nullable().optional(),
  invoiceVatNumber: z.string().nullable().optional(),
  invoiceFirstName: z.string(),
  invoiceLastName: z.string(),
  invoiceStreet: z.string(),
  invoiceCity: z.string(),
  invoiceZip: z.string(),
  returnUrl: z.string(),
  // Meta cookies for Conversions API matching (see readFbCookies). Populated in
  // createOrderFetch, not by the reservation form.
  fbp: z.string().nullish(),
  fbc: z.string().nullish(),
  // GA cookies for the server-side Measurement Protocol purchase (see
  // readGaCookies). Populated in createOrderFetch, not by the reservation form.
  gaClientId: z.string().nullish(),
  gaSessionId: z.string().nullish(),
  // UTM + gclid attribution captured from the landing URL (see
  // captureUtmParams). Populated in createOrderFetch, not by the reservation form.
  utmSource: z.string().nullish(),
  utmMedium: z.string().nullish(),
  utmCampaign: z.string().nullish(),
  utmTerm: z.string().nullish(),
  utmContent: z.string().nullish(),
  gclid: z.string().nullish(),
  // First visit of the customer — the only attribution left when they arrive
  // without campaign params or refuse the cookie consent (see captureUtmParams).
  referrer: z.string().nullish(),
  landingPage: z.string().nullish(),
  firstVisitAt: z.string().nullish(),
})

export const OrderSchema = z.object({
  number: z.string(),
  payment: z
    .object({
      total: z.number(),
      paidAt: z.string().nullable(),
    })
    .nullable(),
  createdAt: z.coerce.date(),
  start: z.coerce.date(),
  end: z.coerce.date(),
  nights: z.number().int(),
  beds: z.number().int(),
  cribs: z.number().int(),
  pets: z.boolean(),
  firstName: z.string(),
  lastName: z.string(),
  email: z.email(),
  phone: z.string(),
  invoice: z.object({
    company: z.string().nullable(),
    cin: z.string().nullable(),
    vatNumber: z.string().nullable(),
    firstName: z.string(),
    lastName: z.string(),
    street: z.string(),
    city: z.string(),
    zip: z.string(),
  }),
  state: z.object({
    id: z.number(),
    state: z.string(),
    name: z.string(),
  }),
  // Set when the location confirms each booking; how it ended follows from the
  // state (requested, paid, declined, expired, cancelled). Times are Prague time.
  request: z
    .object({
      expiresAt: z.string(),
      decidedAt: z.string().nullable(),
      declineReason: z.string().nullable(),
    })
    .nullish(),
  location: LocationSchema.optional(),
  unit: UnitSchema.extend({
    // The unit's own photos; empty means show the location photos.
    photos: z.array(ImageSchema).optional(),
  }).optional(),
})

export const CreateOrderResponseSchema = z.object({
  url: z.string(),
  order: OrderSchema,
})

export const OrderPaymentUrlSchema = z.object({
  url: z.string(),
})

export type OrderRequest = z.infer<typeof OrderRequestSchema>
export type Order = z.infer<typeof OrderSchema>
export type CreateOrderResponse = z.infer<typeof CreateOrderResponseSchema>
export type OrderPaymentUrl = z.infer<typeof OrderPaymentUrlSchema>

// Location inquiry (dotaz na ubytovatele)
export const LocationInquiryRequestSchema = z.object({
  name: z.string(),
  phone: z.string(),
  message: z.string(),
})

export const LocationInquiryResponseSchema = z.looseObject({})

export type LocationInquiryRequest = z.infer<typeof LocationInquiryRequestSchema>
export type LocationInquiryResponse = z.infer<typeof LocationInquiryResponseSchema>

// Location price report (garance nejlepší ceny — user found a better price elsewhere)
export const LocationPriceReportRequestSchema = z.object({
  url: z.string(),
  pricePerNight: z.number(),
  email: z.string(),
})

export const LocationPriceReportResponseSchema = z.looseObject({})

export type LocationPriceReportRequest = z.infer<typeof LocationPriceReportRequestSchema>
export type LocationPriceReportResponse = z.infer<typeof LocationPriceReportResponseSchema>

// Location route (driving distance & duration)
export const LocationRouteSchema = z.object({
  distance: z.number(), // metres
  duration: z.number(), // seconds
})

export type LocationRoute = z.infer<typeof LocationRouteSchema>
